import React from 'react';
import { User } from '../types/User';
import { UserCircle, Users, Calendar, Info, LogOut, Shield } from 'lucide-react';

interface SidebarProps {
  currentUser: User;
  onLogout: () => void;
  onNavigate: (section: string) => void;
  activeSection: string;
}

const Sidebar: React.FC<SidebarProps> = ({ currentUser, onLogout, onNavigate, activeSection }) => {
  return (
    <div className="w-full lg:w-40 shadow-xl border-r border-gray-700 flex flex-row lg:flex-col items-center lg:items-stretch h-14 lg:h-screen lg:sticky lg:top-0 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto flex-shrink-0" style={{ backgroundColor: '#181C1D' }}>
      {/* User Profile Section */}
      <div className="px-2 lg:p-4 border-r lg:border-r-0 lg:border-b border-gray-700 flex-shrink-0">
        <div className="flex lg:flex-col items-center text-center">
          <div 
            className="w-10 lg:w-12 h-10 lg:h-12 rounded-full flex items-center justify-center shadow-lg mb-0 lg:mb-3 cursor-pointer hover:shadow-xl transition-all duration-300 group overflow-hidden"
            style={{ backgroundColor: '#C89116' }}
            onClick={() => onNavigate('profile')}
          >
            {currentUser.avatar ? (
              <img 
                src={currentUser.avatar} 
                alt="Profile" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-sm lg:text-sm font-bold text-gray-900">
                {currentUser.name.split(' ').map(n => n[0]).join('')}
              </span>
            )}
          </div>
          <div 
            className="cursor-pointer transition-all duration-300 group ml-2 lg:ml-0"
            onClick={() => onNavigate('profile')}
          >
            <span 
              className="font-semibold text-xs transition-colors duration-300 hidden lg:block" 
              style={{ 
                color: activeSection === 'profile' ? '#C89116' : 'white'
              }}
            >
              My Profile
            </span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 py-0 lg:py-4 min-w-0">
        <nav className="flex lg:block lg:space-y-4 space-x-2 lg:space-x-0">
          <div className="px-2 lg:px-2 flex-shrink-0">
            <div 
              className={`flex lg:flex-col items-center text-center p-2 rounded-xl cursor-pointer transition-all duration-300 group ${
                activeSection === 'networking' 
                  ? 'shadow-sm' 
                  : 'text-white hover:bg-yellow-900/20 hover:shadow-sm'
              }`}
              style={activeSection === 'networking' ? { color: '#C89116', backgroundColor: 'rgba(200, 145, 22, 0.1)' } : {}}
              onClick={() => onNavigate('networking')}
            >
              <Users 
                className="w-6 lg:w-6 h-6 lg:h-6 mb-0 lg:mb-1 group-hover:scale-110 transition-transform duration-300" 
                style={activeSection === 'networking' ? { color: '#C89116' } : {}}
              />
              <span className="font-semibold text-xs hidden lg:block">Networking</span>
            </div>
          </div>
          
          <div className="px-2 lg:px-2 flex-shrink-0">
            <div 
              className={`flex lg:flex-col items-center text-center p-2 rounded-xl cursor-pointer transition-all duration-300 group ${
                activeSection === 'programme' 
                  ? 'shadow-sm' 
                  : 'text-white hover:bg-yellow-900/20 hover:shadow-sm'
              }`}
              style={activeSection === 'programme' ? { color: '#C89116', backgroundColor: 'rgba(200, 145, 22, 0.1)' } : {}}
              onClick={() => onNavigate('programme')}
            >
              <Calendar 
                className="w-6 lg:w-6 h-6 lg:h-6 mb-0 lg:mb-1 group-hover:scale-110 transition-transform duration-300" 
                style={activeSection === 'programme' ? { color: '#C89116' } : {}}
              />
              <span className="font-semibold text-xs hidden lg:block">Programme</span>
            </div>
          </div>
          
          <div className="px-2 lg:px-2 flex-shrink-0">
            {currentUser.role === 'admin' && (
              <div 
                className={`flex lg:flex-col items-center text-center p-2 rounded-xl cursor-pointer transition-all duration-300 group mb-0 lg:mb-2 ${
                  activeSection === 'admin' 
                    ? 'shadow-sm' 
                    : 'text-white hover:bg-yellow-900/20 hover:shadow-sm'
                }`}
                style={activeSection === 'admin' ? { color: '#C89116', backgroundColor: 'rgba(200, 145, 22, 0.1)' } : {}}
                onClick={() => onNavigate('admin')}
              >
                <Shield 
                  className="w-6 lg:w-6 h-6 lg:h-6 mb-0 lg:mb-1 group-hover:scale-110 transition-transform duration-300" 
                  style={activeSection === 'admin' ? { color: '#C89116' } : {}}
                />
                <span className="font-semibold text-xs hidden lg:block">Admin</span>
              </div>
            )}
            <div 
              className={`flex lg:flex-col items-center text-center p-2 rounded-xl cursor-pointer transition-all duration-300 group ${
                activeSection === 'about' 
                  ? 'shadow-sm' 
                  : 'text-white hover:bg-yellow-900/20 hover:shadow-sm'
              }`}
              style={activeSection === 'about' ? { color: '#C89116', backgroundColor: 'rgba(200, 145, 22, 0.1)' } : {}}
              onClick={() => onNavigate('about')}
            >
              <Info 
                className="w-6 lg:w-6 h-6 lg:h-6 mb-0 lg:mb-1 group-hover:scale-110 transition-transform duration-300" 
                style={activeSection === 'about' ? { color: '#C89116' } : {}}
              />
              <span className="font-semibold text-xs hidden lg:block">About</span>
            </div>
          </div>
        </nav>
      </div>

      {/* Logout Button */}
      <div className="px-2 lg:p-3 border-l lg:border-l-0 lg:border-t border-gray-700 flex-shrink-0">
        <button
          onClick={onLogout}
          className="flex lg:flex-col items-center justify-center space-x-1 lg:space-x-0 lg:space-y-1 p-2 text-white hover:text-red-400 hover:bg-red-900/20 rounded-xl cursor-pointer w-full transition-all duration-300 group hover:shadow-sm"
        >
          <LogOut className="w-5 h-5 lg:w-4 lg:h-4 group-hover:scale-110 transition-transform duration-300" />
          <span className="font-semibold text-xs hidden lg:block">LOGOUT</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;